import { Injectable, Query } from '@angular/core';
import { catchError, combineLatest, concatAll, delay, firstValueFrom, from, map, Observable, ObservableNotification, of, switchMap, tap, toArray } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage, AngularFireUploadTask } from '@angular/fire/compat/storage';
import { AuthService } from './auth.service';
import { IDataService, IDataServiceEntity } from './data.service.interface';
import { StringUtil } from '../lib/string-util';
import { DateUtil } from '../lib/date-util';
import { HttpClient } from '@angular/common/http';

/**
 * in memory store, to be swapped out via configuration for different store using same signatures
 */
@Injectable({
    providedIn: 'root'
})
export class DataService implements IDataService {

    /**
     *
     */
    constructor(private authService: AuthService, private afs: AngularFirestore, private afstg: AngularFireStorage, private httpClient: HttpClient) { 
        this.afs.firestore.settings({ ignoreUndefinedProperties: true });
    }

    /**
     * 
     * @param entityKey 
     * @returns 
     */
    private collectionPath(entityKey: string): string {
        const uid = this.authService.currentState().userInfo?.code;
        if (!uid)
            throw 'Not logged in';

        const ret = `user/${uid}/${entityKey}`;
        return ret;
    }

    /**
     * 
     * @param entityKey 
     * @param entityCode
     * @returns 
     */
    public deleteOne<typeOfEntity extends IDataServiceEntity>(entityKey: string, entityCode: string): Observable<typeOfEntity> {
        const col = this.afs.collection<typeOfEntity>(this.collectionPath(entityKey), ref => ref.where('code', '==', entityCode));
        
        return from(col.get({ source: 'server' })).pipe(
            switchMap(snapshot => {
                if (snapshot.docs.length != 1)
                    throw `Can't find document '${entityCode}'.`;

                return combineLatest([
                    of(snapshot.docs[0].data()),
                    from(snapshot.docs[0].ref.delete())     
                ]).pipe(
                    map(([entity, any]) => ({ entity: entity }))
                )
            }),
            map(ctx => ctx.entity)
        )
    }

    /**
     * 
     * @param entityKey
     * @param entity
     * @returns 
     */
    public saveOne<typeOfEntity extends IDataServiceEntity>(entityKey: string, entity: typeOfEntity): Observable<typeOfEntity> {
        
        const entityToSave: typeOfEntity = {
            ...entity, code: entity.code ?? StringUtil.newId(),
            metadata: { createAt: entity.metadata?.createAt ?? DateUtil.now(), updateAt: DateUtil.now() }
        };

        // new entity
        if (!entity.code) {
            const col = this.afs.collection<typeOfEntity>(this.collectionPath(entityKey));            

            return from(col.add(entityToSave)).pipe(
                switchMap(() => this.loadOne<typeOfEntity>(entityKey, entityToSave.code))
            );
        
        } else {
            const col = this.afs.collection<typeOfEntity>(this.collectionPath(entityKey), ref => ref.where('code', '==', entity.code));
    
            return from(col.get({ source: 'server' })).pipe(
                switchMap(snapshot => {
                    if (snapshot.docs.length != 1)
                        throw `Can't find document '${entity.code}'.`;
                    return from(snapshot.docs[0].ref.set(entityToSave));
                }),
                switchMap(() => this.loadOne<typeOfEntity>(entityKey, entity.code))
            )
        }
    }

    /**
     * 
     * @param entityKey
     * @param entities
     * @returns 
     */
    public saveMany<typeOfEntity extends IDataServiceEntity>(entityKey: string, entities: typeOfEntity[]): Observable<typeOfEntity[]> {        
        return of(entities).pipe(
            concatAll(),
            switchMap(e => this.saveOne(entityKey, e)),
            toArray(),
            switchMap(items => this.loadMany<typeOfEntity>(entityKey, items.map(i => i.code)))
        );
    }

    /**
     * 
     * @param entityKey
     * @param entityCode
     * @returns 
     */
    public loadOne<typeOfEntity extends IDataServiceEntity>(entityKey: string, entityCode: string): Observable<typeOfEntity> {
        const col = this.afs.collection<typeOfEntity>(this.collectionPath(entityKey), ref => ref.where('code', '==', entityCode));    

        return col.get({ source: 'server' }).pipe(
            map(snapshot => {
                const entity: typeOfEntity = snapshot.docs.length > 0 ? snapshot.docs[0].data() : undefined;
                return entity;
            })
        );
    }

    /**
     * 
     * @param entityKey
     * @param entity
     * @returns 
     */
    public loadMany<typeOfEntity extends IDataServiceEntity>(entityKey: string, entityCodes: string[] = undefined): Observable<typeOfEntity[]> {        
        const col = this.afs.collection<typeOfEntity>(this.collectionPath(entityKey), ref => ref.orderBy('metadata.updateAt', 'desc'));    
        return col.get({ source: 'server' }).pipe(
            map(snapshot => snapshot.docs.map(d => d.data())),
            map(entities => {
                const ret = entityCodes ? entities.filter(e => entityCodes.find(ec => ec == e.code)) : entities;
                return ret;
            })
        );
    }

    /**
     * 
     * @param folderName
     * @param fileCode
     * @param encodedFile
     */
    public saveFile(folderName: string, fileCode: string, encodedFile: string): Observable<string> {
        const path = this.filePath(folderName, fileCode);

        return from(this.afstg.ref(path).putString(encodedFile)).pipe(
            switchMap(() => this.loadFile(folderName, fileCode))
        );

        // return from(this.afstg.upload(path, encodedFile)).pipe(
        //     switchMap(() => this.loadFile(folderName, fileCode))
        // );
    }

    /**
     * 
     * @param folderName 
     * @param fileCode 
     * @returns 
     */
    private filePath(folderName: string, fileCode: string): string {
        const uid = this.authService.currentState().userInfo?.code;
        if (!uid)
            throw 'Not logged in';

        const path = `user/${uid}/${folderName}/${fileCode}.data`;
        return path;
    }

    /**
     *
     * @param folderName
     * @param fileCode
     */
    public loadFile(folderName: string, fileCode: string): Observable<string> {
        const path = this.filePath(folderName, fileCode);

        return this.afstg.ref(path).getDownloadURL().pipe(
            switchMap(url => this.httpClient.get(url, { responseType: 'text' })),
            map(result => result),
            catchError(err => of(undefined))
        );
    }    
}
